import React, { useCallback, useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import axios from 'lib/axios'
import Select from 'components/QuestionnaireRequest/AdminNew/components/Select'
import { nanoid } from '@reduxjs/toolkit'
import { clsx } from 'clsx'
import ToggleCheckbox from '../../components/checkbox'

const PROPERTY_TYPES = [
  { value: 'co-op', label: 'Co-op' },
  { value: 'condo', label: 'Condo' },
  { value: 'rental', label: 'Rental' },
  { value: 'cond-op', label: 'Cond-op' },
]

const FormItem = ({
  id,
  form_id: savedFormId,
  property_types: savedSelectedPropertyTypes = [],
  updating,
  onSave,
  onRemove,
  isNew,
}) => {
  const {
    register,
    handleSubmit,
    control,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      form_id: savedFormId || '',
      property_types: savedSelectedPropertyTypes || [],
    },
  })

  const onSubmit = async (formData) => {
    clearErrors()
    const result = await onSave({
      id,
      form_id: formData.form_id,
      property_types: formData.property_types,
      is_new: isNew,
    })

    // Set errors from backend if any
    if (result?.errors) {
      Object.entries(result.errors).forEach(([field, message]) => {
        setError(field, { type: 'manual', message })
      })
    }
  }

  const handleRemove = async () => {
    clearErrors()
    await onRemove(id)
  }

  const hasValidationErrors = Object.values(errors).some((error) => error?.type !== 'manual')
  const disabled = updating || isSubmitting || hasValidationErrors

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="questionnaire-admin-general-row">
      <div className="dome-d-flex dome-flex-column dome-gap6">
        <input
          type="text"
          {...register('form_id', {
            required: 'Form ID is required',
            validate: (value) => (value && value.toString().trim().length > 0) || 'Form ID cannot be empty',
          })}
          readOnly={updating || isSubmitting}
        />
        {errors.form_id && <span className="dome-color-red-alert dome-p3">{errors.form_id.message}</span>}
      </div>
      <div className="dome-d-flex dome-gap12">
        <div className="dome-flex-1 dome-d-flex dome-flex-column dome-gap6">
          <Controller
            name="property_types"
            control={control}
            rules={{
              validate: (value) => (value && value.length > 0) || 'At least one property type is required',
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <Select
                multiple={true}
                onClose={(selectedItems) => onChange(selectedItems)}
                options={PROPERTY_TYPES}
                defaultSelectedItems={value}
                disabled={updating || isSubmitting}
                selectAll={true}
                selectAllDisplay="all property types"
                selectAllOptionLabel="select all types"
              />
            )}
          />
          {errors.property_types && (
            <span className="dome-color-red-alert dome-p3">{errors.property_types.message}</span>
          )}
        </div>
        <div className="dome-d-flex dome-gap12">
          <button
            type="submit"
            className={clsx('dome-btn dome-btn-base dome-btn-aqua-stroke', disabled && 'dome-btn-disabled')}
            disabled={disabled}
          >
            save
          </button>
          <button
            type="button"
            className={clsx(
              'dome-btn dome-btn-base dome-btn-aqua-stroke',
              (updating || isSubmitting) && 'dome-btn-disabled'
            )}
            disabled={updating || isSubmitting}
            onClick={handleRemove}
          >
            remove
          </button>
        </div>
      </div>
      {errors.base && (
        <div className="dome-d-flex">
          <span className="dome-color-red-alert dome-p3">{errors.base.message}</span>
        </div>
      )}
    </form>
  )
}

const FormsList = ({ organizationId, genericFormPreviews, updateGenericFormPreviews }) => {
  const [updating, setUpdating] = useState(false)
  const [formsList, setFormsList] = useState((genericFormPreviews || []).map((item) => ({ ...item, is_new: false })))

  const addNewForm = () => {
    setFormsList((prev) => {
      const newFormsList = [...prev, { id: nanoid(), form_id: '', property_types: [], is_new: true }]
      updateGenericFormPreviews(newFormsList)
      return newFormsList
    })
  }

  const updateFormsList = useCallback(
    (oldId, newData) => {
      setFormsList((prev) => {
        const itemIndex = prev.findIndex((item) => item.id === oldId)
        if (itemIndex === -1) return prev

        const updated = [...prev]
        updated[itemIndex] = newData
        updateGenericFormPreviews(updated)
        return updated
      })
    },
    [updateGenericFormPreviews]
  )

  const onSaveItem = useCallback(
    async (item) => {
      setUpdating(true)
      const url = `/super/organizations/${organizationId}/questionnaires/upsert_questionnaire_generic_setting`
      let itemPayload = { ...item }
      if (item['is_new']) {
        delete itemPayload['id']
      }

      try {
        const { data } = await axios.post(url, { questionnaire_generic_setting: itemPayload })
        const { questionnaire_generic_setting } = data
        updateFormsList(item.id, { ...questionnaire_generic_setting, is_new: false })
        return { success: true }
      } catch (error) {
        // Handle backend errors
        if (error.response?.data?.error) {
          return { errors: error.response.data.error }
        }

        // Handle other errors
        return { errors: { base: error.message || 'An error occurred while saving' } }
      } finally {
        setUpdating(false)
      }
    },
    [organizationId, updateFormsList]
  )

  const onRemoveItem = useCallback(
    async (itemId) => {
      setUpdating(true)
      const url = `/super/organizations/${organizationId}/questionnaires/remove_questionnaire_generic_setting`
      const itemIndex = formsList.findIndex((item) => item.id === itemId)
      if (itemIndex === -1) {
        setUpdating(false)
        return
      }

      try {
        if (!formsList[itemIndex]['is_new']) {
          await axios.post(url, { questionnaire_generic_setting: { id: itemId } })
        }
        setFormsList((prev) => {
          const updated = [...prev]
          updated.splice(itemIndex, 1)
          updateGenericFormPreviews(updated)
          return updated
        })
      } catch (error) {
        console.error('Error removing item:', error)
        const errorMessage =
          error.response?.data?.error?.base ||
          error.response?.data?.error ||
          error.message ||
          'An error occurred while removing'
        alert(errorMessage)
      } finally {
        setUpdating(false)
      }
    },
    [formsList, organizationId]
  )

  const renderFormsList = () => {
    return formsList.map((item) => (
      <FormItem
        key={item.id}
        id={item.id}
        form_id={item.form_id}
        property_types={item.property_types}
        onSave={onSaveItem}
        onRemove={onRemoveItem}
        updating={updating}
        isNew={item.is_new}
      />
    ))
  }

  return (
    <div className="dome-d-flex dome-flex-column dome-gap6">
      <div className="questionnaire-admin-general-row">
        <span className="dome-p2 dome-color-med-grey">form ID*</span>
        <span className="dome-p2 dome-color-med-grey">property type*</span>
      </div>
      {renderFormsList()}
      <div className="dome-d-flex dome-align-start">
        <button className="dome-btn dome-btn-link dome-btn-link-aqua" onClick={addNewForm}>
          + form
        </button>
      </div>
    </div>
  )
}

const EnableCheckbox = ({ organizationId, enabled, updateEnabled }) => {
  const [updating, setUpdating] = useState(false)

  const changeValue = async (enabled) => {
    try {
      setUpdating(true)
      const url = `/super/organizations/${organizationId}/questionnaires/enable_generic_preview`
      await axios.post(url, { is_generic_preview_on: enabled ? 1 : 0 })
      updateEnabled(enabled)
    } catch (error) {
      console.log(error)
    } finally {
      setUpdating(false)
    }
  }

  return (
    <ToggleCheckbox
      label="allow preview"
      defaultChecked={enabled}
      disabled={updating}
      onChange={(e) => changeValue(e.target.checked)}
    />
  )
}

export default function GenericPreviews({
  organizationId,
  isGenericPreviewEnabled,
  updateIsGenericPreviewEnabled,
  genericFormPreviews,
  updateGenericFormPreviews,
}) {
  return (
    <div className="dome-d-flex dome-flex-column dome-gap6">
      <div className="questionnaire-admin-general-row">
        <EnableCheckbox
          organizationId={organizationId}
          enabled={isGenericPreviewEnabled}
          updateEnabled={updateIsGenericPreviewEnabled}
        />
      </div>
      <FormsList
        organizationId={organizationId}
        genericFormPreviews={genericFormPreviews}
        updateGenericFormPreviews={updateGenericFormPreviews}
      />
    </div>
  )
}
